<template>
  <div class="fts-settings-page">
    <header class="fts-settings-head">
      <div>
        <h1>GNey</h1>
        <p>How the AI teammate behaves in this workspace.</p>
      </div>
    </header>

    <section class="fts-settings-card">
      <div>
        <h2>Workspace instructions</h2>
        <p class="fts-settings-card-sub">
          Standing instructions GNey follows in every conversation and automation in this
          workspace &mdash; language, terminology, defaults and procedure. They cannot override
          platform rules such as approvals, permissions and confirmations. For behaviour that
          belongs to one module, use the module designer&rsquo;s AI annotations instead.
        </p>
      </div>

      <div class="fts-settings-loading" v-if="loading">
        <fts-spinner :size="16" />
        <span>Loading&hellip;</span>
      </div>

      <div class="fts-settings-banner is-warn" v-else-if="error">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>{{ error }}</span>
      </div>

      <template v-else>
        <textarea
          v-model="instructions"
          class="fts-settings-textarea"
          :maxlength="maxLength"
          rows="10"
          aria-label="Workspace instructions"
          placeholder="e.g. Always answer in Malay. Customers are called 'members'. Amounts are in MYR. Flag anything above RM10,000 to the Finance team before proposing it."
        ></textarea>

        <div class="fts-settings-textarea-meta">
          <span>Takes effect within a minute.</span>
          <span>{{ (instructions || "").length }} / {{ maxLength }}</span>
        </div>

        <div style="display: flex; align-items: center; gap: 12px;">
          <button
            type="button"
            class="fts-settings-btn is-primary"
            :disabled="saving || instructions === original"
            @click="save"
          >
            {{ saving ? "Saving…" : "Save instructions" }}
          </button>
          <button
            type="button"
            class="fts-settings-btn"
            v-if="instructions !== original"
            :disabled="saving"
            @click="instructions = original"
          >
            Discard
          </button>
        </div>
      </template>
    </section>

    <section class="fts-settings-card">
      <div>
        <h2>Who can use GNey</h2>
        <p class="fts-settings-card-sub">
          GNey is on for every member, and every request draws on the workspace&rsquo;s AI
          credits. Turning it off for one person arrives with People &amp; seats.
        </p>
      </div>
      <div>
        <a href="#" @click.prevent="goCredits">See where credits went</a>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * Settings › GNey (alpha, P0 slice).
 *
 * The workspace instructions move out of the dialog's AI Teammate tab unchanged in
 * substance — same endpoints, same 4,000-character ceiling, same "server echoes the
 * sanitized text" contract. The per-member GNey switch (R3) needs Member.GneyDisabled,
 * which is ruled but not in the code, so this page only says where it will live.
 */
import { store as canvasDesignStore } from "../../store/canvasDesignStore";
import FTSSpinner from "../../elements/FTSSpinner";

export default {
  name: "SettingsGney",
  components: { "fts-spinner": FTSSpinner },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      saving: false,
      error: "",
      instructions: "",
      original: "",
      maxLength: 4000,
    };
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await canvasDesignStore.dispatch(
          "retrieveTeammateInstructions",
          this.workspaceId
        );
        const data = (res && res.data) || {};
        this.instructions = data.instructions || "";
        if (data.maxLength) this.maxLength = data.maxLength;
        this.original = this.instructions;
      } catch (err) {
        this.error = this.message(err, "These instructions are not available right now.");
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.saving) return;
      this.saving = true;
      try {
        const res = await canvasDesignStore.dispatch("saveTeammateInstructions", {
          workspaceId: this.workspaceId,
          instructions: this.instructions,
        });
        // The server echoes the sanitized text (control characters stripped, trimmed).
        this.instructions = (res && res.data && res.data.instructions) || "";
        this.original = this.instructions;
        this.$toasted.success("Instructions saved");
      } catch (err) {
        this.$toasted.global.error(err, this.message(err, "That was not saved."));
      } finally {
        this.saving = false;
      }
    },
    message(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && (data.message || data.error_description)) || fallback;
    },
    goCredits() {
      this.$router.push({ name: "settings", params: { section: "credits" } }).catch(() => {});
    },
  },
  watch: {
    workspaceId() {
      this.load();
    },
  },
  created() {
    this.load();
  },
};
</script>
